/**
 * Input normalization — the engine's front door.
 *
 * A parser never sees a raw `File` or a raw string. Every caller path (file
 * picker, drag-and-drop, paste) funnels through here and produces one shape,
 * so no parser has to branch on how the template happened to arrive. Six
 * parsers each re-deriving "is this a File or a string?" is six chances to
 * disagree about it.
 *
 * `text` is always populated. Binary formats (PSD, PDF) will extend this shape
 * with an `arrayBuffer` field rather than change `text` — adding a field is
 * backward compatible for parsers already written against it.
 */

/**
 * Read a Blob/File as text. `Blob.prototype.text` is the modern path; the
 * FileReader fallback exists because it is the only reader available in older
 * Safari, which is still a supported browser for the admin surface.
 */
const readAsText = (blob) => {
  if (typeof blob.text === 'function') return blob.text();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsText(blob);
  });
};

/**
 * Byte length of a string, not its character count — they differ for any
 * non-ASCII content, and template names routinely carry it.
 *
 * `TextEncoder` is the fast path but is absent from jsdom, so tests would
 * otherwise exercise a different code path than production — the shape of bug
 * that hides until it ships. `Blob` covers jsdom and every supported browser.
 */
const utf8Length = (text) => {
  if (typeof TextEncoder === 'function') return new TextEncoder().encode(text).length;
  if (typeof Blob === 'function') return new Blob([text]).size;
  return text.length;
};

/**
 * Normalize a raw input into the shape parsers consume.
 *
 * @param {File|Blob|string} raw
 * @returns {Promise<import('./types').Input>}
 */
export const normalizeInput = async (raw) => {
  if (typeof raw === 'string') {
    return {
      kind: 'text',
      text: raw,
      filename: '',
      mimeType: '',
      byteLength: utf8Length(raw),
    };
  }

  if (raw && typeof raw === 'object' && typeof raw.size === 'number') {
    const text = await readAsText(raw);
    return {
      kind: 'file',
      text,
      filename: typeof raw.name === 'string' ? raw.name : '',
      mimeType: typeof raw.type === 'string' ? raw.type : '',
      // The file's own byte count, not the decoded text's: for a future binary
      // parser this is the only honest measure of what arrived.
      byteLength: raw.size,
    };
  }

  throw new TypeError('normalizeInput expects a File, a Blob, or a string');
};
